import { create } from 'zustand';
import api from '@/lib/api';

export type TargetType = 'followed' | 'inspirator' | 'impress';
export type AlertFrequency = 'instant' | 'daily' | 'weekly' | 'off';

export interface Target {
  id: string;
  user_id: string;
  persona_id?: string | null;
  linkedin_profile_id: string;
  linkedin_url?: string;
  nickname: string | null;
  notes: string | null;
  type?: TargetType;
  priority?: 'high' | 'medium' | 'low' | null;
  notify_on_post?: boolean;
  notify_on_comment?: boolean;
  alert_frequency?: AlertFrequency | string | null;
  relationship_tags?: string[];
  is_active?: boolean;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  full_name: string | null;
  headline: string | null;
  followers_count: number | null;
  profile_picture_url: string | null;
}

export interface AddTargetInput {
  linkedin_url: string;
  nickname?: string;
  notes?: string;
  persona_id?: string;
  type?: TargetType;
  priority?: 'high' | 'medium' | 'low';
  notify_on_post?: boolean;
  notify_on_comment?: boolean;
  alert_frequency?: AlertFrequency;
  relationship_tags?: string[];
  is_active?: boolean;
}

export interface UpdateTargetInput {
  nickname?: string;
  notes?: string;
  is_favorite?: boolean;
  persona_id?: string | null;
  type?: TargetType;
  priority?: 'high' | 'medium' | 'low';
  notify_on_post?: boolean;
  notify_on_comment?: boolean;
  alert_frequency?: AlertFrequency;
  relationship_tags?: string[];
  is_active?: boolean;
}

interface TargetsState {
  targets: Target[];
  isLoading: boolean;
  error: string | null;
  fetchTargets: () => Promise<void>;
  addTarget: (input: AddTargetInput) => Promise<void>;
  removeTarget: (id: string) => Promise<void>;
  updateTarget: (id: string, data: UpdateTargetInput) => Promise<void>;
  getStats: () => { total: number; favoriteCount: number; activeAlerts: number; impressCount: number };
}

function parseApiError(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  ) {
    const response = (error as {
      response?: { data?: { error?: string; message?: string; detail?: string } };
    }).response;
    return response?.data?.error ?? response?.data?.message ?? response?.data?.detail ?? fallback;
  }
  return fallback;
}

export const useTargetsStore = create<TargetsState>()((set, get) => ({
  targets: [],
  isLoading: false,
  error: null,

  fetchTargets: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: Target[] }>('/targets');
      set({ targets: response.data.data ?? [], isLoading: false });
    } catch (error: unknown) {
      set({ error: parseApiError(error, 'Failed to fetch network people'), isLoading: false });
    }
  },

  addTarget: async (input) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Target }>('/targets', input);
      const newTarget = response.data.data;
      set((state) => ({ targets: [newTarget, ...state.targets], isLoading: false }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to add network person');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  removeTarget: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/targets/${id}`);
      set((state) => ({ targets: state.targets.filter((target) => target.id !== id), isLoading: false }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to remove network person');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  updateTarget: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: Target }>(`/targets/${id}`, data);
      const updatedTarget = response.data.data;
      set((state) => ({
        targets: state.targets.map((target) => (target.id === id ? { ...target, ...updatedTarget } : target)),
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to update network person');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  getStats: () => {
    const targets = get().targets;
    return {
      total: targets.length,
      favoriteCount: targets.filter((target) => target.is_favorite).length,
      activeAlerts: targets.filter((target) => target.notify_on_post || target.notify_on_comment).length,
      impressCount: targets.filter((target) => target.type === 'impress').length,
    };
  },
}));
